import { Controller } from "@hotwired/stimulus";

// Connect lending calculator (SUR-2302 / SSD-19 + the SSD-18/LO "Refinance"
// tab). Three modes — Affordability ("how much house can I afford"), Mortgage
// ("monthly payment for a price") and Refinance ("estimate potential savings").
// The affordability + refinance math is ported from the ezhomesearch webapp
// (app/javascript/controllers/calculators/{affordability,refinance}_controller.js);
// the payment breakdown is drawn as a CSS conic-gradient donut so the public
// bundle doesn't need Chart.js.
//
// Server-rendered markup lives in connect/shared/_mortgage_calculator; this
// controller reads the inputs, recomputes on change, and paints the results.
const CHART = {
  pi: "#f1c03e",       // principal & interest (yellow)
  taxes: "#008db0",    // property taxes (blue)
  insurance: "#b8c873",// homeowner's insurance (lime)
  hoa: "#e16167",      // HOA dues (red)
};

export default class extends Controller {
  static targets = [
    "mode", "tab",
    // shared purchase inputs
    "downPayment", "rate", "term", "insurance", "hoa", "taxPct", "purchaseFields",
    // affordability-only inputs
    "income", "debts", "dti", "dtiLabel", "affordFields",
    // mortgage-only inputs
    "homePrice", "mortgageFields",
    // refinance-only inputs
    "refiFields", "refiBalance", "refiRate", "refiTerm", "refiNewAmount", "refiNewRate", "refiNewTerm",
    // outputs
    "priceLabel", "priceValue", "payment", "paymentLabel", "donut", "breakdown",
    "legendPi", "legendTaxes", "legendInsurance", "legendHoa",
    "refiResults", "refiImpact", "formHeading", "note",
  ];
  static values = { mode: { type: String, default: "affordability" } };

  // Per-mode form heading + helper copy (from the design's three data views).
  static HEADINGS = {
    affordability: "How much house can I afford?",
    mortgage: "Calculate your payment",
    refinance: "Estimate potential savings",
  };
  static NOTES = {
    affordability: "Enter your income, debts, and down payment to see how much home you may qualify for. Estimates only — not a lending decision.",
    mortgage: "Adjust the home price, down payment, and loan term to estimate your monthly payment. Estimates only — not a lending decision.",
    refinance: "Update your current loan details and new rate to see your potential monthly savings. Estimates only — not a lending decision.",
  };

  connect() {
    this.recompute();
  }

  switchMode(event) {
    this.modeValue = event.currentTarget.dataset.mode;
    const mode = this.modeValue;
    this.tabTargets.forEach((t) => t.classList.toggle("is-active", t.dataset.mode === mode));
    // affordFields covers both the income/debt inputs AND the DTI slider block.
    this.affordFieldsTargets.forEach((el) => { el.hidden = mode !== "affordability"; });
    if (this.hasMortgageFieldsTarget) this.mortgageFieldsTarget.hidden = mode !== "mortgage";
    if (this.hasRefiFieldsTarget) this.refiFieldsTarget.hidden = mode !== "refinance";
    if (this.hasPurchaseFieldsTarget) this.purchaseFieldsTarget.hidden = mode === "refinance";
    if (this.hasBreakdownTarget) this.breakdownTarget.hidden = mode === "refinance";
    if (this.hasRefiResultsTarget) this.refiResultsTarget.hidden = mode !== "refinance";
    if (this.hasFormHeadingTarget) this.formHeadingTarget.textContent = this.constructor.HEADINGS[mode];
    if (this.hasNoteTarget) this.noteTarget.textContent = this.constructor.NOTES[mode];
    this.recompute();
  }

  recompute() {
    if (this.modeValue === "refinance") {
      this.recomputeRefinance();
      return;
    }

    const num = this.num;
    const rate = num(this.rateTarget, 6);
    const termMonths = num(this.termTarget, 360);
    const insuranceAnnual = num(this.insuranceTarget, 0);
    const hoa = num(this.hoaTarget, 0);
    const taxPct = num(this.taxPctTarget, 0);
    const insuranceMonthly = insuranceAnnual / 12;

    let homePrice;
    let principalAndInterest;

    if (this.modeValue === "mortgage") {
      homePrice = num(this.homePriceTarget, 0);
      const downPayment = num(this.downPaymentTarget, 0);
      const loan = Math.max(homePrice - downPayment, 0);
      principalAndInterest = this.monthlyPayment(loan, rate, termMonths);
    } else {
      const income = num(this.incomeTarget, 0);
      const debts = num(this.debtsTarget, 0);
      const downPayment = num(this.downPaymentTarget, 0);
      const dti = num(this.dtiTarget, 34) / 100;
      const grossMonthlyIncome = income / 12;
      // Housing budget under the chosen DTI. Property tax must be reserved
      // INSIDE this budget, not added after the price is solved — otherwise the
      // displayed payment overshoots the DTI the user picked. Tax scales with
      // the (unknown) home price, so solve the loan in closed form from
      //   P&I(loan) + (loan + down) * taxRate/12 = budget
      // where A is the amortized payment per $1 of loan.
      const budget = Math.max(dti * grossMonthlyIncome - debts - insuranceMonthly - hoa, 0);
      const taxMonthlyRate = taxPct / 100 / 12;
      const a = this.monthlyPayment(1, rate, termMonths);
      const denom = a + taxMonthlyRate;
      const loan = denom > 0 ? Math.max((budget - downPayment * taxMonthlyRate) / denom, 0) : 0;
      principalAndInterest = loan * a;
      homePrice = loan + downPayment;
      this.updateDti(dti * 100);
    }

    const monthlyTax = (homePrice * (taxPct / 100)) / 12;
    const total = principalAndInterest + monthlyTax + insuranceMonthly + hoa;

    this.paintDonut(principalAndInterest, monthlyTax, insuranceMonthly, hoa);
    if (this.hasPriceValueTarget) this.priceValueTarget.textContent = this.money(homePrice);
    if (this.hasPaymentTarget) this.paymentTarget.textContent = this.money(total);
    if (this.hasPriceLabelTarget) {
      this.priceLabelTarget.textContent = this.modeValue === "mortgage" ? "Home price" : "You can afford";
    }
    if (this.hasPaymentLabelTarget) this.paymentLabelTarget.textContent = "Monthly payment";
  }

  // Refinance — ported from the ez webapp's refinance_controller: monthly
  // savings = current P&I minus new P&I. The "term impact" figure answers the
  // design's "N fewer payments if yearly savings are applied to loan principal"
  // by amortizing the new loan with the savings redirected into principal.
  recomputeRefinance() {
    const num = this.num;
    const oldPayment = this.monthlyPayment(
      num(this.refiBalanceTarget, 0), num(this.refiRateTarget, 0), num(this.refiTermTarget, 360)
    );
    const newAmount = num(this.refiNewAmountTarget, 0);
    const newRate = num(this.refiNewRateTarget, 0);
    const newTerm = num(this.refiNewTermTarget, 360);
    const newPayment = this.monthlyPayment(newAmount, newRate, newTerm);
    const savings = oldPayment - newPayment;

    if (this.hasPriceValueTarget) this.priceValueTarget.textContent = this.money(Math.max(savings, 0));
    if (this.hasPriceLabelTarget) this.priceLabelTarget.textContent = "Monthly savings";
    if (this.hasPaymentTarget) this.paymentTarget.textContent = this.money(newPayment);
    if (this.hasPaymentLabelTarget) this.paymentLabelTarget.textContent = "New monthly payment";

    if (this.hasRefiImpactTarget) {
      const fewer = savings > 0 ? this.fewerPayments(newAmount, newRate, newTerm, newPayment, savings) : 0;
      this.refiImpactTarget.textContent = `${fewer} fewer payment${fewer === 1 ? "" : "s"}`;
    }
  }

  // Months saved on the new loan when the monthly savings are paid as extra
  // principal. Simple amortization simulation, capped at the new term.
  fewerPayments(principal, ratePct, termMonths, payment, extra) {
    const mRate = ratePct / 100 / 12;
    if (principal <= 0 || payment + extra <= principal * mRate) return 0;
    let balance = principal;
    let months = 0;
    while (balance > 0 && months < termMonths) {
      balance = balance + balance * mRate - (payment + extra);
      months += 1;
    }
    return Math.max(termMonths - months, 0);
  }

  // Standard amortized monthly P&I (mirrors ez's calculateMonthlyMortgagePayment).
  monthlyPayment(loan, ratePct, termMonths) {
    if (loan <= 0 || termMonths <= 0) return 0;
    const mRate = ratePct / 100 / 12;
    if (mRate <= 0) return loan / termMonths;
    return (mRate * loan * Math.pow(1 + mRate, termMonths)) / (Math.pow(1 + mRate, termMonths) - 1);
  }

  paintDonut(pi, taxes, insurance, hoa) {
    const total = pi + taxes + insurance + hoa || 1;
    let acc = 0;
    const stops = [
      [CHART.pi, pi], [CHART.taxes, taxes], [CHART.insurance, insurance], [CHART.hoa, hoa],
    ].map(([color, val]) => {
      const start = (acc / total) * 360;
      acc += val;
      const end = (acc / total) * 360;
      return `${color} ${start}deg ${end}deg`;
    });
    if (this.hasDonutTarget) this.donutTarget.style.background = `conic-gradient(${stops.join(", ")})`;
    if (this.hasLegendPiTarget) this.legendPiTarget.textContent = this.money(pi);
    if (this.hasLegendTaxesTarget) this.legendTaxesTarget.textContent = this.money(taxes);
    if (this.hasLegendInsuranceTarget) this.legendInsuranceTarget.textContent = this.money(insurance);
    if (this.hasLegendHoaTarget) this.legendHoaTarget.textContent = this.money(hoa);
  }

  updateDti(ratio) {
    if (!this.hasDtiLabelTarget) return;
    const desc = ratio < 30 ? "Excellent" : ratio < 36 ? "Comfortable" : ratio < 43 ? "Manageable" : "High";
    this.dtiLabelTarget.textContent = `${Math.round(ratio)}% • ${desc}`;
  }

  num(t, fallback = 0) {
    if (!t) return fallback;
    const v = parseFloat(String(t.value).replace(/[^0-9.]/g, ""));
    return Number.isFinite(v) ? v : fallback;
  }

  money(v) {
    if (!Number.isFinite(v)) return "—";
    return `$${Math.round(v).toLocaleString("en-US")}`;
  }
}
